import { useCallback, useState, useEffect } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppDispatch, NotificationFrequencyTypes, CollectionFilesyncAPI, useSendRequestMutation, DriveRequest, ErrorStackItem,
  Message, getEnumKey, MessageHandlerDisplayType, DriveWatchedErrorMessages, NotificationFrequencyMapToType, NotificationFrequencyMapValue, DriveWatchedMessages
} from "src/common";
import { uiActions, DriveCallbackAsyncFunction } from "src/store";

export function useEmailFrequency(current_account: Account) {
  const [frequency, setFrequency] = useState<NotificationFrequencyTypes>();
  const [loading, setLoading] = useState<boolean>(true);
  const [sendRequest] = useSendRequestMutation();
  const dispatch = useAppDispatch();

  // Description: Decode all paths
  useEffect(() => {
    handleGetEmailFrequency();
  }, []);

  // Description: handle saved email frequency
  async function handleGetEmailFrequency() {
    setLoading(true);
    const drive_request = await CollectionFilesyncAPI.getWatchedEmailFrequency(current_account);
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const saved = message.getFrequency();
        saved !== undefined ?
          handleSuccess(saved) :
          handlePageErrorMessage(DriveWatchedErrorMessages.error_setting_email_frequency, { stack_message: DriveWatchedErrorMessages.error_setting_email_frequency }, message,
            MessageHandlerDisplayType.toastr);
      } else {
        handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_email_frequency }, message,
          MessageHandlerDisplayType.toastr);
      }
    }
    handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_email_frequency);
  }

  // Description: Set new email frequency
  async function handleSetEmailFrequency(new_frequency: NotificationFrequencyMapValue) {
    setLoading(true);
    const drive_request = await CollectionFilesyncAPI.setWatchedEmailFrequency(current_account, new_frequency);
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        handleSuccess(new_frequency);
        dispatch(uiActions.handleSetMessage(new Message(DriveWatchedMessages.success_setting_email_frequency)));
      } else {
        handlePageErrorMessage(DriveWatchedErrorMessages.error_setting_email_frequency, { stack_message: DriveWatchedErrorMessages.error_setting_email_frequency }, message,
          MessageHandlerDisplayType.toastr);
      }
    }
    handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_setting_email_frequency);
  }

  // Description: Reset form state to active
  function handleSuccess(saved: NotificationFrequencyMapValue) {
    const _frequency = NotificationFrequencyMapToType[saved];
    setFrequency(_frequency);
    setLoading(false);
  }

  // Description: Reset form state to active
  function reset() {
    setLoading(false);
  }

  const setEmailFrequency = useCallback((new_frequency: NotificationFrequencyMapValue) => {
    dispatch(uiActions.handleMessageDismiss());
    handleSetEmailFrequency(new_frequency);
  }, []);


  // -----------------------------
  // HOOK General
  // -----------------------------
  // Description: Sends Request and handles error when the request object is null, pass it custom stack message
  function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveWatchedErrorMessages.error_sending_request) {
    if (!!request) {
      request.setCallback(callback);
      sendRequest(request);
    } else {
      handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_error, stack_message: DriveWatchedErrorMessages.error_sending_request });
    }
  }
  // Description:  send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useEmailFrequency Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    reset();
  }

  return {
    frequency,
    loading,
    setEmailFrequency
  };
}
